import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLanguage, useAuth, api } from '@/lib/context';
import { getCountryByCode } from '@/lib/countries-data';
import { useTranslatedContent } from '@/hooks/useContentTranslation';
import { TranslatableText } from '@/components/TranslatableText';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Briefcase, Building2, MapPin, Calendar, Euro, Clock, Users,
  Plus, Eye, Send, Lock, Unlock, CheckCircle2, XCircle, 
  Loader2, ChevronRight, Filter, ArrowLeft, Mail, Phone,
  FileText, Shield, AlertCircle, Home, Languages
} from 'lucide-react';
import { toast } from 'sonner';
import { ShareButton, ShareButtonsInline } from '@/components/ShareButton';
import { SEOHead } from '@/components/SEOHead';
import { LocationAutocomplete } from '@/components/LocationAutocomplete';

// Categories for subcontracting
const SUBCONTRACT_CATEGORIES = [
  'construction', 'electrical', 'plumbing', 'hvac', 'painting',
  'flooring', 'roofing', 'landscaping', 'demolition', 'welding',
  'carpentry', 'masonry', 'transportation', 'logistics', 'cleaning',
  'it_services', 'consulting', 'other'
];

// Back to Home Link Component
const BackToHomeLink = () => {
  const { t } = useLanguage();
  return (
    <Link 
      to="/" 
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
      data-testid="back-to-home-link"
    >
      <Home className="h-4 w-4" />
      <span>{t('subcontracting.backToHome')}</span>
    </Link>
  );
};

// ============================================================
// SUBCONTRACTING LIST PAGE
// ============================================================

export const SubcontractingPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    country: ''
  });

  useEffect(() => {
    fetchJobs();
  }, [filters]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.country) params.append('country', filters.country);
      
      const res = await api.get(`/subcontracting/jobs?${params.toString()}`);
      setJobs(res.data);
    } catch (error) {
      console.error('Failed to fetch subcontracting jobs:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Fixed Language Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      
      {/* Header */}
      <div className="bg-gradient-to-br from-brand/10 to-brand/5 border-b">
        <div className="container mx-auto px-4 py-12">
          <BackToHomeLink />
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            {t('subcontracting.title')}
          </h1>
          <p className="text-lg text-muted-foreground mb-6">
            {t('subcontracting.subtitle')}
          </p>
          
          {user?.role === 'company' && (
            <Link to="/subcontracting/create">
              <Button className="gap-2" data-testid="create-job-btn">
                <Plus className="h-4 w-4" />
                {t('subcontracting.postJob')}
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-4 mb-6">
          <Select value={filters.category} onValueChange={(v) => setFilters({...filters, category: v === 'all' ? '' : v})}>
            <SelectTrigger className="w-48" data-testid="category-filter">
              <SelectValue placeholder={t('subcontracting.filterCategory')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('subcontracting.allCategories')}</SelectItem>
              {SUBCONTRACT_CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>
                  {t(`categories.${cat}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="w-56">
            <LocationAutocomplete
              type="country"
              value={filters.country}
              onChange={(v) => setFilters({...filters, country: v})}
              placeholder={t('subcontracting.filterCountry')}
              data-testid="country-filter"
              allowFreeText={true}
            />
          </div>
        </div>

        {/* Jobs Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map(i => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2 mb-4" />
                  <Skeleton className="h-20 w-full mb-4" />
                  <Skeleton className="h-8 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {t('subcontracting.noJobs')}
              </h3>
              <p className="text-muted-foreground">
                {t('subcontracting.noJobsDesc')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.map(job => (
              <SubcontractJobCard key={job.id} job={job} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// JOB CARD COMPONENT
// ============================================================

const SubcontractJobCard = ({ job }) => {
  const { t } = useLanguage();
  const { text: translatedTitle, isTranslated: titleTranslated } = useTranslatedContent(job.title);
  const { text: translatedDesc, isTranslated: descTranslated } = useTranslatedContent(job.description);
  const isContentTranslated = titleTranslated || descTranslated;
  
  const country = getCountryByCode(job.country);
  // Use localized country name from i18n - NO defaultValue fallback
  const countryKey = `location.countries.${job.country}`;
  const translatedCountry = t(countryKey);
  const countryName = translatedCountry !== countryKey ? translatedCountry : (country?.name || job.country);

  // Category translation - NO defaultValue fallback
  const categoryKey = `categories.${job.category}`;
  const translatedCategory = t(categoryKey);
  const categoryDisplay = translatedCategory !== categoryKey ? translatedCategory : job.category;

  return (
    <Card className="hover:shadow-lg transition-shadow" data-testid={`job-card-${job.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {job.company_logo ? (
              <img 
                src={job.company_logo.startsWith('http') ? job.company_logo : `${process.env.REACT_APP_BACKEND_URL}${job.company_logo}`}
                alt={job.company_name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-brand" />
              </div>
            )}
            <div>
              <p className="text-sm font-medium">{job.company_name}</p>
              {job.company_verified && (
                <Badge variant="secondary" className="text-xs">
                  <Shield className="h-3 w-3 mr-1" />
                  {t('company.verified')}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge>{categoryDisplay}</Badge>
            <ShareButton 
              entityType="subcontract" 
              entityId={job.id} 
              title={job.title}
              iconOnly
            />
          </div>
        </div>
        <CardTitle className="mt-3 text-lg line-clamp-2">
          {translatedTitle}
          {isContentTranslated && (
            <Languages className="inline-block ml-2 h-3.5 w-3.5 text-blue-500" title={t('common.translated')} />
          )}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground line-clamp-2">{translatedDesc}</p>
        
        <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            {job.city ? `${job.city}, ` : ''}{countryName}
          </span>
          {job.deadline && (
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {new Date(job.deadline).toLocaleDateString()}
            </span>
          )}
        </div>

        {(job.budget_min || job.budget_max) && (
          <div className="flex items-center gap-1 text-sm font-medium">
            <Euro className="h-4 w-4" />
            {job.budget_min && job.budget_max 
              ? `${job.budget_min.toLocaleString()} - ${job.budget_max.toLocaleString()} ${job.currency}`
              : job.budget_min 
                ? `${t('subcontracting.from')} ${job.budget_min.toLocaleString()} ${job.currency}`
                : `${t('subcontracting.upTo')} ${job.budget_max.toLocaleString()} ${job.currency}`
            }
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {job.bid_count} {t('subcontracting.bids')}
            </span>
            <ShareButtonsInline 
              entityType="subcontract" 
              entityId={job.id} 
              title={job.title}
            />
          </div>
          <Link to={`/subcontracting/${job.id}`}>
            <Button size="sm" data-testid={`view-job-${job.id}`}>
              {t('common.viewDetails')}
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================
// JOB DETAIL PAGE
// ============================================================

export const SubcontractingDetailPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { jobId } = useParams();
  const navigate = useNavigate();
  
  const [job, setJob] = useState(null);
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBidModal, setShowBidModal] = useState(false);
  
  // Dynamic content translation - call hooks unconditionally with fallback values
  const { text: translatedTitle, isTranslated: titleTranslated } = useTranslatedContent(job?.title || '');
  const { text: translatedDesc, isTranslated: descTranslated } = useTranslatedContent(job?.description || '');
  const isContentTranslated = titleTranslated || descTranslated;

  useEffect(() => {
    fetchJob();
    fetchSeoData();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const res = await api.get(`/subcontracting/jobs/${jobId}`);
      setJob(res.data);
    } catch (error) {
      console.error('Failed to fetch job:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSeoData = async () => {
    try {
      const res = await api.get(`/public/subcontracting/${jobId}`);
      setSeoData(res.data);
    } catch (error) {
      console.log('SEO data not available');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <Skeleton className="h-8 w-48 mb-6" />
          <Skeleton className="h-64 w-full mb-6" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {t('subcontracting.notFound')}
          </h2>
          <Link to="/subcontracting">
            <Button variant="outline">{t('common.back')}</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const countryDetail = getCountryByCode(job.country);
  const countryKeyDetail = `location.countries.${job.country}`;
  const translatedCountryDetail = t(countryKeyDetail);
  const countryNameDetail = translatedCountryDetail !== countryKeyDetail ? translatedCountryDetail : (countryDetail?.name || job.country);
  const isOwner = user?.id === job.company_id;
  const canBid = user?.role === 'company' && !isOwner;
  const hasBid = job.bids?.some(b => b.bidder_company_id === user?.id);
  
  // Category translation for detail view
  const categoryKeyDetail = `categories.${job.category}`;
  const translatedCategoryDetail = t(categoryKeyDetail);
  const categoryDisplayDetail = translatedCategoryDetail !== categoryKeyDetail ? translatedCategoryDetail : job.category;

  return (
    <div className="min-h-screen bg-background py-8">
      {/* SEO Head */}
      {seoData && (
        <SEOHead
          title={seoData.meta?.title || job?.title}
          description={seoData.meta?.description || job?.description?.substring(0, 160)}
          url={seoData.share_url || window.location.href}
          schema={seoData.schema}
          type="article"
        />
      )}
      
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Back to home and back to list */}
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <BackToHomeLink />
          <span className="text-muted-foreground">|</span>
          <Button variant="ghost" size="sm" className="h-auto p-0 text-muted-foreground hover:text-foreground" onClick={() => navigate('/subcontracting')}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('subcontracting.backToList')}
          </Button>
        </div>

        {/* Job Details Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {job.company_logo ? (
                  <img 
                    src={job.company_logo.startsWith('http') ? job.company_logo : `${process.env.REACT_APP_BACKEND_URL}${job.company_logo}`}
                    alt={job.company_name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-brand/10 flex items-center justify-center">
                    <Building2 className="h-8 w-8 text-brand" />
                  </div>
                )}
                <div>
                  <p className="font-medium">{job.company_name}</p>
                  {job.company_verified && (
                    <Badge variant="secondary">
                      <Shield className="h-3 w-3 mr-1" />
                      {t('company.verified')}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-lg px-3 py-1">
                  {categoryDisplayDetail}
                </Badge>
                <ShareButton 
                  entityType="subcontract" 
                  entityId={job.id} 
                  title={job.title}
                  iconOnly
                />
              </div>
            </div>
            
            <CardTitle className="text-2xl mt-4">
              {translatedTitle}
              {isContentTranslated && (
                <Languages className="inline-block ml-2 h-4 w-4 text-blue-500" title={t('common.translated')} />
              )}
            </CardTitle>
            
            <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {job.city ? `${job.city}, ` : ''}{countryNameDetail}
              </span>
              {job.deadline && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {t('subcontracting.deadline')}: {new Date(job.deadline).toLocaleDateString()}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                {job.view_count} {t('common.views')}
              </span>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Budget */}
            {(job.budget_min || job.budget_max) && (
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium mb-2">{t('subcontracting.budget')}</h4>
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <Euro className="h-5 w-5" />
                  {job.budget_min && job.budget_max 
                    ? `${job.budget_min.toLocaleString()} - ${job.budget_max.toLocaleString()} ${job.currency}`
                    : job.budget_min 
                      ? `${t('subcontracting.from')} ${job.budget_min.toLocaleString()} ${job.currency}`
                      : `${t('subcontracting.upTo')} ${job.budget_max.toLocaleString()} ${job.currency}`
                  }
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <h4 className="font-medium mb-2">{t('subcontracting.description')}</h4>
              <p className="text-muted-foreground whitespace-pre-wrap">{translatedDesc}</p>
            </div>

            {/* Requirements */}
            {job.requirements?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">{t('subcontracting.requirements')}</h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  {job.requirements.map((req, i) => (
                    <li key={i}>
                      <TranslatableText>{req}</TranslatableText>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Owner Contact (if unlocked by bidder) */}
            {job.owner_contact_unlocked && (
              <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-4">
                <h4 className="font-medium mb-2 flex items-center gap-2 text-green-700 dark:text-green-400">
                  <Unlock className="h-4 w-4" />
                  {t('subcontracting.companyContact')}
                </h4>
                {job.owner_contact_email && (
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${job.owner_contact_email}`} className="text-brand hover:underline">
                      {job.owner_contact_email}
                    </a>
                  </div>
                )}
                {job.owner_contact_phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a href={`tel:${job.owner_contact_phone}`} className="text-brand hover:underline">
                      {job.owner_contact_phone}
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4 border-t">
              {canBid && !hasBid && (
                <Button onClick={() => setShowBidModal(true)} className="gap-2" data-testid="submit-bid-btn">
                  <Send className="h-4 w-4" />
                  {t('subcontracting.submitBid')}
                </Button>
              )}
              
              {canBid && hasBid && (
                <Badge variant="secondary" className="text-base py-2 px-4">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {t('subcontracting.bidSubmitted')}
                </Badge>
              )}
              
              {!user && (
                <Link to="/login">
                  <Button>{t('auth.loginToBid')}</Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bids Section */}
        {(isOwner || hasBid) && job.bids?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('subcontracting.bids')} ({job.bids.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {job.bids.map(bid => (
                <BidCard key={bid.id} bid={bid} job={job} isOwner={isOwner} onUpdate={fetchJob} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Bid Modal */}
        <BidModal 
          open={showBidModal} 
          onClose={() => setShowBidModal(false)} 
          jobId={job.id}
          onSuccess={() => {
            setShowBidModal(false);
            fetchJob();
          }}
        />
      </div>
    </div>
  );
};

// ============================================================
// BID CARD COMPONENT
// ============================================================

const BidCard = ({ bid, job, isOwner, onUpdate }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [unlocking, setUnlocking] = useState(false);
  const [unlockStatus, setUnlockStatus] = useState(null);

  useEffect(() => {
    if (isOwner && !bid.contact_unlocked) {
      checkUnlockStatus();
    }
  }, [bid.id]);

  const checkUnlockStatus = async () => {
    try {
      const res = await api.get(`/subcontracting/unlock/status?subcontract_job_id=${job.id}&target_company_id=${bid.bidder_company_id}`);
      setUnlockStatus(res.data);
    } catch (error) {
      console.error('Failed to check unlock status:', error);
    }
  };

  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      const res = await api.post(`/subcontracting/unlock/create-checkout?subcontract_job_id=${job.id}&target_company_id=${bid.bidder_company_id}`);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setUnlocking(false);
    }
  };

  const isMine = bid.bidder_company_id === user?.id;

  return (
    <div className={`border rounded-lg p-4 ${bid.status === 'accepted' ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {bid.bidder_company_logo ? (
            <img 
              src={bid.bidder_company_logo.startsWith('http') ? bid.bidder_company_logo : `${process.env.REACT_APP_BACKEND_URL}${bid.bidder_company_logo}`}
              alt={bid.bidder_company_name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-brand" />
            </div>
          )}
          <div>
            <p className="font-medium">{bid.bidder_company_name}</p>
            {bid.bidder_verified && (
              <Badge variant="secondary" className="text-xs">
                <Shield className="h-3 w-3 mr-1" />
                {t('company.verified')}
              </Badge>
            )}
          </div>
        </div>
        
        <Badge variant={bid.status === 'accepted' ? 'default' : bid.status === 'rejected' ? 'destructive' : 'secondary'}>
          {t(`subcontracting.bidStatus.${bid.status}`) || bid.status}
        </Badge>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-3">
        <div>
          <p className="text-sm text-muted-foreground">{t('subcontracting.bidPrice')}</p>
          <p className="font-semibold text-lg">€{bid.price.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{t('subcontracting.estimatedDays')}</p>
          <p className="font-semibold">{bid.estimated_days} {t('common.days')}</p>
        </div>
        {bid.team_size && (
          <div>
            <p className="text-sm text-muted-foreground">{t('subcontracting.teamSize')}</p>
            <p className="font-semibold">{bid.team_size}</p>
          </div>
        )}
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        <TranslatableText>{bid.message}</TranslatableText>
      </p>

      {/* Contact Info */}
      {bid.contact_unlocked && (
        <div className="bg-green-50 dark:bg-green-950/30 rounded p-3 mb-3">
          <p className="text-sm font-medium text-green-700 dark:text-green-400 mb-2">
            <Unlock className="h-4 w-4 inline mr-1" />
            {t('subcontracting.contactInfo')}
          </p>
          {bid.contact_email && (
            <a href={`mailto:${bid.contact_email}`} className="text-sm text-brand hover:underline block">
              {bid.contact_email}
            </a>
          )}
          {bid.contact_phone && (
            <a href={`tel:${bid.contact_phone}`} className="text-sm text-brand hover:underline block">
              {bid.contact_phone}
            </a>
          )}
        </div>
      )}

      {/* Unlock Button (for job owner) */}
      {isOwner && !bid.contact_unlocked && unlockStatus && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleUnlock}
          disabled={unlocking}
          className="gap-2"
        >
          {unlocking ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Lock className="h-4 w-4" />
          )}
          {unlockStatus.is_free 
            ? t('unlock.unlockFree')
            : `${t('unlock.unlockFor')} €${unlockStatus.price}`
          }
        </Button>
      )}

      {/* Accept/Reject buttons (for job owner) */}
      {isOwner && bid.status === 'pending' && (
        <div className="flex gap-2 mt-3 pt-3 border-t">
          <Button 
            size="sm" 
            className="gap-1"
            onClick={async () => {
              try {
                await api.put(`/subcontracting/bids/${bid.id}`, { status: 'accepted' });
                toast.success(t('subcontracting.bidAccepted'));
                onUpdate();
              } catch (error) {
                toast.error(error.response?.data?.detail || t('common.error'));
              }
            }}
          >
            <CheckCircle2 className="h-4 w-4" />
            {t('subcontracting.accept')}
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={async () => {
              try {
                await api.put(`/subcontracting/bids/${bid.id}`, { status: 'rejected' });
                toast.success(t('subcontracting.bidRejected'));
                onUpdate();
              } catch (error) {
                toast.error(error.response?.data?.detail || t('common.error'));
              }
            }}
          >
            <XCircle className="h-4 w-4" />
            {t('subcontracting.reject')}
          </Button>
        </div>
      )}
    </div>
  );
};

// ============================================================
// BID MODAL
// ============================================================

const BidModal = ({ open, onClose, jobId, onSuccess }) => {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    price: '',
    estimated_days: '',
    message: '',
    team_size: '',
    includes_materials: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await api.post('/subcontracting/bids', {
        subcontract_job_id: jobId,
        price: parseFloat(form.price),
        estimated_days: parseInt(form.estimated_days),
        message: form.message,
        team_size: form.team_size ? parseInt(form.team_size) : null,
        includes_materials: form.includes_materials
      });
      
      toast.success(t('subcontracting.bidSuccess'));
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('subcontracting.submitBid')}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t('subcontracting.yourPrice')} *</Label>
            <Input
              type="number"
              min="1"
              step="0.01"
              required
              value={form.price}
              onChange={(e) => setForm({...form, price: e.target.value})}
              placeholder="5000"
              data-testid="bid-price-input"
            />
          </div>
          
          <div>
            <Label>{t('subcontracting.estimatedDays')} *</Label>
            <Input
              type="number"
              min="1"
              required
              value={form.estimated_days}
              onChange={(e) => setForm({...form, estimated_days: e.target.value})}
              placeholder="30"
              data-testid="bid-days-input"
            />
          </div>
          
          <div>
            <Label>{t('subcontracting.teamSize')}</Label>
            <Input
              type="number"
              min="1"
              value={form.team_size}
              onChange={(e) => setForm({...form, team_size: e.target.value})}
              placeholder="5"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Switch
              checked={form.includes_materials}
              onCheckedChange={(v) => setForm({...form, includes_materials: v})}
            />
            <Label>{t('subcontracting.includesMaterials')}</Label>
          </div>
          
          <div>
            <Label>{t('subcontracting.message')} *</Label>
            <Textarea
              required
              minLength={10}
              value={form.message}
              onChange={(e) => setForm({...form, message: e.target.value})}
              placeholder={t('subcontracting.messagePlaceholder')}
              rows={4}
              data-testid="bid-message-input"
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting} data-testid="submit-bid-form-btn">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('subcontracting.submitBid')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// CREATE JOB PAGE
// ============================================================

export const SubcontractingCreatePage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    country: '',
    city: '',
    deadline: '',
    budget_min: '',
    budget_max: '',
    requirements: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const res = await api.post('/subcontracting/jobs', {
        title: form.title,
        description: form.description,
        category: form.category,
        country: form.country,
        city: form.city || null,
        deadline: form.deadline || null,
        budget_min: form.budget_min ? parseFloat(form.budget_min) : null,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : null,
        requirements: form.requirements ? form.requirements.split('\n').filter(r => r.trim()) : []
      });
      
      toast.success(t('subcontracting.jobCreated'));
      navigate(`/subcontracting/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (user?.role !== 'company') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md text-center p-8">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {t('subcontracting.companyOnly')}
          </h2>
          <p className="text-muted-foreground mb-4">
            {t('subcontracting.companyOnlyDesc')}
          </p>
          <Link to="/login">
            <Button>{t('auth.login')}</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Back navigation */}
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <BackToHomeLink />
          <span className="text-muted-foreground">|</span>
          <Button variant="ghost" size="sm" className="h-auto p-0 text-muted-foreground hover:text-foreground" onClick={() => navigate('/subcontracting')}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('subcontracting.backToList')}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('subcontracting.postJob')}</CardTitle>
            <CardDescription>
              {t('subcontracting.postJobDesc')}
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div>
                <Label>{t('subcontracting.jobTitle')} *</Label>
                <Input
                  required
                  minLength={5}
                  value={form.title}
                  onChange={(e) => setForm({...form, title: e.target.value})}
                  placeholder={t('subcontracting.titlePlaceholder')}
                  data-testid="job-title-input"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>{t('subcontracting.category')} *</Label>
                  <Select 
                    required
                    value={form.category} 
                    onValueChange={(v) => setForm({...form, category: v})}
                  >
                    <SelectTrigger data-testid="job-category-select">
                      <SelectValue placeholder={t('common.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBCONTRACT_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>
                          {t(`categories.${cat}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>{t('subcontracting.country')} *</Label>
                  <LocationAutocomplete
                    type="country"
                    value={form.country}
                    onChange={(v) => setForm({...form, country: v, city: ''})}
                    placeholder={t('subcontracting.countryPlaceholder')}
                    data-testid="job-country-select"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>{t('subcontracting.city')}</Label>
                  <LocationAutocomplete
                    type="city"
                    value={form.city}
                    onChange={(v) => setForm({...form, city: v})}
                    countryCode={form.country}
                    placeholder={t('subcontracting.cityPlaceholder')}
                    data-testid="job-city-input"
                  />
                </div>
                
                <div>
                  <Label>{t('subcontracting.deadline')}</Label>
                  <Input
                    type="date"
                    value={form.deadline}
                    onChange={(e) => setForm({...form, deadline: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>{t('subcontracting.budgetMin')}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.budget_min}
                    onChange={(e) => setForm({...form, budget_min: e.target.value})}
                    placeholder="5000"
                  />
                </div>
                
                <div>
                  <Label>{t('subcontracting.budgetMax')}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.budget_max}
                    onChange={(e) => setForm({...form, budget_max: e.target.value})}
                    placeholder="15000"
                  />
                </div>
              </div>
              
              <div>
                <Label>{t('subcontracting.description')} *</Label>
                <Textarea
                  required
                  minLength={20}
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  placeholder={t('subcontracting.descriptionPlaceholder')}
                  rows={6}
                  data-testid="job-description-input"
                />
              </div>
              
              <div>
                <Label>{t('subcontracting.requirements')}</Label>
                <Textarea
                  value={form.requirements}
                  onChange={(e) => setForm({...form, requirements: e.target.value})}
                  placeholder={t('subcontracting.requirementsPlaceholder')}
                  rows={4}
                />
              </div>
            </CardContent>
            
            <CardFooter>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="post-job-btn">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('subcontracting.postJob')}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default SubcontractingPage;
